import Dataman from 'App/Dataman';
import { FEAT_ID_FILLING_MATERIAL, FEAT_ID_REGION } from 'Lib/enums/HardcodedIds';
import { FT_LIST, FT_RANGE, FT_CONDITION_GROUP } from '../../FilterTypes';
import { sortBy } from 'App/utils/Helpers';
import filterItemsByRole from '../helpers/filterItemsByRole';
import { isEmpty } from '@/components/filters/Utils';

const FEATURE_GROUP_INGREDIENT_CATEGORY_ID = 1;

/**
 * @param {{name: string, groupOrderIndex: number}} groupConf
 */
export async function loadFeatures(groupConf, params = {}, { favoriteConditionGroup = false } = {}) {
  const itemsPerPage = Number(params?.itemsPerPage) || 1000;
  const initialPage = Number(params?.page) || 1;
  const requestParams = {
    ...params,
    itemsPerPage,
  };

  const getMember = (response) => response?.data?.['hydra:member'] || response?.data || [];
  const getTotal = (response, itemsLength) => Number(response?.data?.['hydra:totalItems']) || itemsLength;

  const firstRes = await Dataman.Filter.getProductFeatures({
    re: true,
    params: {
      ...requestParams,
      page: initialPage,
    },
  });

  let allFeatures = getMember(firstRes);
  const totalItems = getTotal(firstRes, allFeatures.length);

  if (totalItems > allFeatures.length) {
    let page = initialPage + 1;
    while (allFeatures.length < totalItems) {
      const pageRes = await Dataman.Filter.getProductFeatures({
        re: true,
        params: {
          ...requestParams,
          page,
        },
      });
      const pageItems = getMember(pageRes);
      if (!pageItems.length) {
        break;
      }
      allFeatures = allFeatures.concat(pageItems);
      page += 1;
    }
  }

  if (allFeatures.length) {
    /** @type {Map<number, {id: number, name: string}>} */
    const childFeaturesList = new Map();
    allFeatures.forEach(({ id, name, optionsChildFeatures }) => {
      if (!isEmpty(optionsChildFeatures)) {
        Object.values(optionsChildFeatures).forEach((featureId) => childFeaturesList.set(featureId, { id, name }));
      }
    });
    return allFeatures
      .reduce((acc, props) => {
        if ([FEAT_ID_REGION, FEAT_ID_FILLING_MATERIAL].includes(props.id)) {
          return acc;
        }
        const { id, name, values, categories, hasOptions } = props;
        const valuesList = Object.entries(values || {})
          .map(([id, name]) => ({ id, name }))
          .sort(sortBy('text'));
        let group = { name: 'Features', groupOrderIndex: 0, isFeature: true, ...(groupConf || {}) };
        if (childFeaturesList.has(id)) {
          const parent = childFeaturesList.get(id);
          group = {
            name: parent.name,
            groupOrderIndex: (groupConf?.groupOrderIndex || 100) + parent.id,
            isFeature: true,
          };
        }
        const asFavoriteConditionGroup = favoriteConditionGroup && hasOptions;
        acc.push({
          id: `feature${id}`,
          label: name,
          type: hasOptions ? (asFavoriteConditionGroup ? FT_CONDITION_GROUP : FT_LIST) : FT_RANGE,
          group,
          featureID: id,
          isFeature: true,
          // useStrategies: true,
          // hasNotStrategy: true,
          useNASearch: true,
          categories: categories?.map((el) => el.id),
          valuesList,
          ...(asFavoriteConditionGroup && {
            placeholder: 'Choose value',
            fieldConf: {
              isFavorite: true,
              favoriteKind: 'featureOptions',
            },
          }),
        });
        return acc;
      }, [])
      .sort((a, b) => {
        if (a.label.toLowerCase() > b.label.toLowerCase()) {
          return 1;
        } else if (a.label.toLowerCase() < b.label.toLowerCase()) {
          return -1;
        } else {
          return 0;
        }
      });
  }
  return [];
}

export async function loadFeatureListValues(id, conf = {}) {
  const feature = await loadFeature(id, conf);
  if (!feature || !feature?.hasOptions) {
    return [];
  }
  const result = Object.entries(feature.values).reduce((res, [key, value], i) => {
    res[i] = {
      id: key,
      name: value,
    };
    return res;
  }, Array(feature.values.length));
  const filtered = result.filter((item) => filterItemsByRole(item, id));
  return filtered.sort((a, b) => {
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    } else if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    } else {
      return 0;
    }
  });
}

export async function loadIngredientCategories() {
  return Dataman.DB.featureGroups
    .getList({ 'parent.id': FEATURE_GROUP_INGREDIENT_CATEGORY_ID, properties: ['id', 'name'], itemsPerPage: 200 })
    .then((res) => (res.data || []).map(({ id, name }) => ({ id, name })).sort(sortBy('name')));
}

async function loadFeature(id, conf = {}) {
  return Dataman.Filter.getProductFeature(id, conf).then(
    (res) => {
      return res.data;
    },
    (err) => {
      console.error(err);
      return null;
    }
  );
}

export default {
  loadFeatures,
  loadFeatureListValues,
  loadIngredientCategories,
};
